<script lang="ts" setup>
import {onBeforeUnmount, onMounted, toRefs, watch} from 'vue';
import {useAppSession} from '../core/app-session';
import {useNotificationExpiry, useNotifications} from '../core/notifications';
import Navbar from './layout/Navbar.vue';
import VersionNotification from './layout/VersionNotification.vue';
import {acceptReferral} from '../api/users';
import {acceptStoredReferral} from './referrals/referrals';

const appSession = useAppSession();
const notifications = useNotifications();
const {warnings, infos} = toRefs(notifications.state);
useNotificationExpiry(notifications.state);

async function associateStoredReferral() {
    if (!appSession.state.auth?.user?.id) return;
    try {
        await acceptStoredReferral(localStorage, acceptReferral);
    } catch {
        // Keep the invitation in storage and retry on the next authenticated app load.
    }
}

watch(() => appSession.state.auth?.user?.id, associateStoredReferral, {immediate: true});

// Refresh the current user's subscription and store it in the session state.
function refreshSubscriptionStatus() {
    if (!appSession.state.auth?.user?.id) {
        return;
    }
    void appSession.refreshSubscription(appSession.state.auth.user.id);
}

// Re-check the subscription only when the app tab becomes visible again.
function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
        refreshSubscriptionStatus();
    }
}

// Register the visibility listener when the root app mounts.
onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Warm up Paddle on the landing page so checkout opens without reinitializing the SDK.
    if (appSession.state.auth) {
        void appSession.initializePaddle().catch((): undefined => undefined);
    }
});

// Remove the visibility listener when the root app unmounts.
onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>
<template>
    <Navbar></Navbar>
    <div class="flex flex-grow flex-col px-3">
        <div class="toast toast-top toast-center z-50">
            <template v-if="infos.length">
                <div
                    v-for="infoMessage in infos"
                    :key="infoMessage.id"
                    class="alert alert-info">
                    <span>{{ infoMessage.message }}</span>
                </div>
            </template>
            <template v-if="warnings.length">
                <div
                    v-for="warningMessage in warnings"
                    :key="warningMessage.id"
                    class="alert alert-warning">
                    <span>{{ warningMessage.message }}</span>
                </div>
            </template>
        </div>
        <router-view></router-view>
    </div>
    <VersionNotification />
    <datalist id="presetColors">
        <!-- 32 colors -->
        <!-- Grayscale -->
        <option>#FFFFFF</option>
        <option>#EEEEEE</option>
        <option>#BDBDBD</option>
        <option>#757575</option>
        <option>#424242</option>
        <option>#000000</option>

        <!-- Material UI Colors -->
        <option label="Red">#F44336</option>
        <option label="Pink">#E91E63</option>
        <option label="Purple">#9C27B0</option>
        <option label="Deep Purple">#673AB7</option>
        <option label="Indigo">#3F51B5</option>
        <option label="Blue">#2196F3</option>
        <option label="Light Blue">#03A9F4</option>
        <option label="Cyan">#00BCD4</option>
        <option label="Teal">#009688</option>
        <option label="Green">#4CAF50</option>
        <option label="Light Green">#8BC34A</option>
        <option label="Lime">#CDDC39</option>
        <option label="Yellow">#FFEB3B</option>
        <option label="Amber">#FFC107</option>
        <option label="Orange">#FF8200</option>
        <option label="Deep Orange">#FF5722</option>
        <option label="Brown">#795548</option>
        <option label="Blue Grey">#607D8B</option>
    </datalist>
</template>
<style lang="css">
body {
    visibility: visible !important;
}

.pixelated {
    image-rendering: pixelated;
}
</style>
